import React from "react";
import { Plus, CheckCircle2, Check } from "lucide-react";
import "./EligibilityTab.css";

const summaryStats = [
  { label: "Eligible Users", value: "3,200" },
  { label: "Excluded Users", value: "420" },
  { label: "Pending Check", value: "85" },
  { label: "Total Evaluated", value: "3,705" },
];

const includeRules = [
  {
    id: 1,
    rule: "Customer Segment",
    condition: "is",
    value: "VIP Customers",
    match: "AND",
    status: "Active",
  },
  {
    id: 2,
    rule: "Loyalty Tier",
    condition: "is one of",
    value: "Gold, Platinum, Diamond",
    match: "AND",
    status: "Active",
  },
  {
    id: 3,
    rule: "Total Orders Count",
    condition: "greater than or equal",
    value: "10",
    match: "AND",
    status: "Active",
  },
  {
    id: 4,
    rule: "Total Spent (EGP)",
    condition: "greater than or equal",
    value: "1000",
    match: "AND",
    status: "Active",
  },
];

const excludeRules = [
  {
    id: 1,
    rule: "Existing Voucher Users",
    condition: "is",
    value: "Yes",
    match: "OR",
    status: "Active",
  },
  {
    id: 2,
    rule: "Account Status",
    condition: "is",
    value: "Suspended",
    match: "OR",
    status: "Active",
  },
  {
    id: 3,
    rule: "Employee Accounts",
    condition: "is",
    value: "Internal Employee",
    match: "OR",
    status: "Active",
  },
  {
    id: 4,
    rule: "Test / Dummy Accounts",
    condition: "is",
    value: "Yes",
    match: "OR",
    status: "Active",
  },
];

const couponRules = [
  { label: "Discount Type", value: "Fixed Amount" },
  { label: "Discount Value", value: "150 EGP" },
  { label: "Minimum Order Value", value: "0 EGP" },
  { label: "Maximum Discount", value: "150 EGP" },
  { label: "Usage Limit", value: "1 time per customer" },
  { label: "Stackable", value: "No" },
  { label: "Applicable Categories", value: "All Categories" },
  { label: "Payment Method", value: "All Methods" },
  { label: "Validity", value: "Mar 10 - May 31, 2025" },
];

const validationChecklist = [
  "Coupon is active",
  "Discount cap is defined",
  "Rules configured correctly",
  "Usage limit is valid",
  "Target audience size is valid",
];

// ================== SUB COMPONENTS ==================
function StepBadge({ n }) {
  return <span className="et-step-badge">{n}</span>;
}

function RulesTable({ rows }) {
  return (
    <div className="et-table-wrap">
      <table className="et-table">
        <thead>
          <tr>
            <th className="et-col-num">#</th>
            <th>Rule</th>
            <th>Condition</th>
            <th></th>
            <th>Match</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="et-col-num">{r.id}</td>
              <td>{r.rule}</td>
              <td>{r.condition}</td>
              <td>{r.value}</td>
              <td>{r.match}</td>
              <td>
                <span className="et-status-pill">{r.status}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ================== MAIN COMPONENT ==================
export default function EligibilityTab() {
  return (
    <div className="et-grid">
      {/* ---------- MAIN COLUMN ---------- */}
      <div className="et-main">
        <div className="et-panel">
          {/* 1. Eligibility Summary */}
          <div className="et-panel-section">
            <div className="et-card-header">
              <StepBadge n={1} />
              <h3 className="et-card-title">Eligibility Summary</h3>
            </div>
            <p className="et-desc">
              This coupon is available to users who meet all the eligibility
              conditions below.
            </p>

            <div className="et-summary-grid">
              {summaryStats.map((s) => (
                <div className="et-summary-item" key={s.label}>
                  <span className="et-summary-label">{s.label}</span>
                  <span className="et-summary-value">{s.value}</span>
                </div>
              ))}
            </div>

            <div className="et-status-block">
              <span className="et-summary-label">Eligibility Status</span>
              <span className="et-eligibility-pill">Active</span>
            </div>
          </div>

          <div className="et-panel-divider" />

          {/* 2. Include Users */}
          <div className="et-panel-section">
            <div className="et-card-header-row">
              <div className="et-card-header">
                <StepBadge n={2} />
                <h3 className="et-card-title">Include Users</h3>
              </div>
              <button className="et-add-btn et-add-btn-include">
                <Plus size={14} /> Add Condition
              </button>
            </div>
            <p className="et-desc">Define who is eligible to use this coupon</p>

            <RulesTable rows={includeRules} />
          </div>

          <div className="et-panel-divider" />

          {/* 3. Exclude Users */}
          <div className="et-panel-section">
            <div className="et-card-header-row">
              <div className="et-card-header">
                <StepBadge n={3} />
                <h3 className="et-card-title">Exclude Users</h3>
              </div>
              <button className="et-add-btn et-add-btn-exclude">
                <Plus size={14} /> Add Condition
              </button>
            </div>
            <p className="et-desc">
              Define who is not eligible to use this coupon
            </p>

            <RulesTable rows={excludeRules} />
          </div>
        </div>
      </div>

      {/* ---------- SIDEBAR ---------- */}
      <div className="et-sidebar">
        {/* Rule Health */}
        <div className="et-card">
          <h3 className="et-card-title">Rule Health</h3>
          <div className="et-health-row">
            <span className="et-health-icon">
              <CheckCircle2 size={18} color="#fff" />
            </span>
            <span className="et-health-title">Healthy</span>
          </div>
          <p className="et-health-line">All rules are valid</p>
          <p className="et-health-sub">No conflicts detected</p>
        </div>

        {/* Coupon Rules */}
        <div className="et-card pb-0">
          <h3 className="et-rg-card-title">Coupon Rules</h3>
          <div className="et-rules-list py-3">
            {couponRules.map((r, i) => (
              <div className="et-rule-item" key={r.label}>
                <span className="et-rule-num">{i + 1}</span>
                <span className="et-rule-label">{r.label}</span>
                <span className="et-rule-value">{r.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Validation Checklist */}
        <div className="et-card pb-0">
          <h3 className="et-rg-card-title">Validation Checklist</h3>
          <div className="et-checklist py-3">
            {validationChecklist.map((item) => (
              <div className="et-check-item" key={item}>
                <span className="et-check-icon">
                  <Check size={12} color="#fff" />
                </span>
                <span className="et-check-label">{item}</span>
                <span className="et-pass-pill">Pass</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
